import { Address, ServiceResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';

export type AddressState = {
  data: Address[];
  selectedAddress: Address | null;
  isLoading: boolean;
  error: ServiceResponse<unknown> | null;
};

export type AddressActions = {
  setSelectedAddress: (address: Address | null) => void;
  fetchData: () => Promise<ServiceResponse<Address[]>>;
  createAddress: (address: AddressUpdateParams) => Promise<ServiceResponse<Address>>;
  updateAddress: (address: AddressUpdateParams) => Promise<ServiceResponse<Address>>;
  deleteAddress: (rowKey: string) => Promise<ServiceResponse<unknown>>;
  clear: () => void;
};

export type AddressStore = AddressState & AddressActions;

export type AddressUpdateParams = {
  name?: string;
  addressName?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  zipcode?: string;
  provinceCode?: string;
  provinceName?: string;
  districtCode?: string;
  districtName?: string;
  subDistrictCode?: string;
  subDistrictName?: string;
  contactNumber?: string;
  countryCode?: string;
  countryName?: string;
  isDefault?: boolean;
  rowKey?: string;
};

export const useAddressStore = create<AddressStore>((set, get) => ({
  // Initial state
  data: [],
  selectedAddress: null,
  isLoading: false,
  error: null,

  setSelectedAddress: (address: Address | null) => set({ selectedAddress: address }),

  fetchData: async () => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.userAddresses();
    set({ isLoading: false });

    if (response.type === 'success') {
      set({
        data: response.model,
        error: null,
      });
    } else {
      set({ error: response });
    }

    return response;
  },

  createAddress: async (address: AddressUpdateParams) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.updateAddress({
      ...address,
      rowKey: '',
    });
    set({ isLoading: false });

    if (response.type === 'success') {
      // Refresh address list
      get().fetchData();
      set({ error: null });
    } else {
      set({ error: response });
    }

    return response;
  },

  updateAddress: async (address: AddressUpdateParams) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.updateAddress({
      ...address,
      rowKey: address.rowKey || '',
    });
    set({ isLoading: false });

    if (response.type === 'success') {
      // Refresh address list
      get().fetchData();
      set({ error: null });
    } else {
      set({ error: response });
    }

    return response;
  },

  deleteAddress: async (rowKey: string) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.deleteAddress({ rowKey });
    set({ isLoading: false });

    if (response.type === 'success') {
      // Refresh address list
      get().fetchData();
      set({ error: null });
    } else {
      set({ error: response });
    }

    return response;
  },

  clear: () => {
    set({
      data: [],
      selectedAddress: null,
      isLoading: false,
      error: null,
    });
  },
}));
